import {
  ChangeDetectionStrategy,
  Component,
  NgZone,
  OnDestroy,
} from '@angular/core';
import { Location } from '@angular/common';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import { defaultCancelReasons } from 'server/helpers/defaultCancelReasons';
import { randomId } from 'server/shared/helpers/random';
import { AvailabilityStatusAfterCancelEnum } from 'server/shared/models/availabilityStatusEnum';
import { CancelReason } from 'server/shared/models/cancelReason';
import { Customer } from 'server/shared/models/customer';
import { CustomerService } from '../../services/customer.service';
import { PermissionsService } from '../../services/permissions.service';
import { ToastService } from '../../services/toast/toast.service';
@Component({
  selector: 'admin-demand-reasons',
  templateUrl: 'cancel-reasons.html',
  styleUrls: ['cancel-reasons.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CancelReasonsComponent implements OnDestroy {
  customer$ = new BehaviorSubject<Customer | undefined>(undefined);

  currentUserTracker?: Tracker.Computation;
  private destroyed$ = new Subject<void>();

  cancelReasons$ = new BehaviorSubject<CancelReason[]>([]);
  initialCancelReasons: CancelReason[] = [];
  cancelReasonsInCreation$ = new BehaviorSubject<
    {
      displayName: { de: string; en: string; fr: string };
      enableDescription?: boolean;
      statusAfterCancel?: AvailabilityStatusAfterCancelEnum;
    }[]
  >([]);
  availableStatuses$ = new BehaviorSubject(AvailabilityStatusAfterCancelEnum);

  // Getters for backward compatibility
  get customer() {
    return this.customer$.value;
  }

  get cancelReasons() {
    return this.cancelReasons$.value;
  }

  get cancelReasonsInCreation() {
    return this.cancelReasonsInCreation$.value;
  }

  constructor(
    private ngZone: NgZone,
    private location: Location,
    private toastService: ToastService,
    private customerService: CustomerService,
    private permissionService: PermissionsService,
  ) {
    this.customerService.readyDispoCustomer$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((customer) => {
        this.customer$.next(customer);
        this.initializeCancelReason();
      });
    this.permissionService.isCustomerAdmin$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isCustomerAdmin) => {
        if (!isCustomerAdmin) {
          this.historyBack();
        }
      });
  }

  ngOnInit(): void {
    this.currentUserTracker = Tracker.autorun(() => {
      this.ngZone.run(() => {
        this.initializeCancelReason();
      });
    });
  }

  initializeCancelReason() {
    if (!this.customer$.value) return;

    let cancelReasonFromCustomer = JSON.parse(
      JSON.stringify(
        this.customer$.value.cancelReasons || defaultCancelReasons,
      ),
    );

    if (this.cancelReasons$.value.length === 0) {
      this.cancelReasons$.next([...cancelReasonFromCustomer]);
    } else {
      let currentReasons = [...this.cancelReasons$.value];
      cancelReasonFromCustomer.forEach(
        (cancelReasonFromCustomer: CancelReason) => {
          let cancelReasonIndex = currentReasons.findIndex((cancelReason) => {
            return cancelReason.id === cancelReasonFromCustomer.id;
          });
          if (cancelReasonIndex === -1) {
            currentReasons.push(cancelReasonFromCustomer);
          }
          if (
            this.initialCancelReasons[cancelReasonIndex] &&
            currentReasons[cancelReasonIndex].displayName ===
              this.initialCancelReasons[cancelReasonIndex].displayName
          ) {
            currentReasons[cancelReasonIndex] = cancelReasonFromCustomer;
          }
        },
      );
      this.cancelReasons$.next(currentReasons);
    }
    this.initialCancelReasons = cancelReasonFromCustomer;
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
    this.currentUserTracker?.stop();
  }

  historyBack() {
    this.location.back();
  }

  addNewCancelReason() {
    const current = [...this.cancelReasonsInCreation$.value];
    current.push({
      displayName: { de: '', en: '', fr: '' },
    });
    this.cancelReasonsInCreation$.next(current);
  }

  cancelNewCancelReason(index: number) {
    const current = [...this.cancelReasonsInCreation$.value];
    current.splice(index, 1);
    this.cancelReasonsInCreation$.next(current);
  }

  saveNewCancelReason(index: number) {
    let displayName = this.cancelReasonsInCreation$.value[index]?.displayName;
    if (!displayName || !this.customer$.value) return;
    let newCancelReason: CancelReason = {
      id: randomId(),
      displayName: displayName,
      enableDescription:
        this.cancelReasonsInCreation$.value[index]?.enableDescription,
      statusAfterCancel:
        this.cancelReasonsInCreation$.value[index]?.statusAfterCancel,
    };

    const currentReasons = [...this.cancelReasons$.value];
    currentReasons.push(newCancelReason);
    this.cancelReasons$.next(currentReasons);

    const currentInCreation = [...this.cancelReasonsInCreation$.value];
    currentInCreation.splice(index, 1);
    this.cancelReasonsInCreation$.next(currentInCreation);

    Meteor.call(
      'addCancelReason',
      this.customer$.value._id,
      newCancelReason,
      (error: Meteor.Error) => {
        if (error) {
          this.toastService.add({
            type: 'ERROR',
            message: error.error?.toString(),
          });
        } else {
          this.toastService.add({ type: 'SUCCESS', message: 'changesSaved' });
        }
      },
    );
  }

  deleteCancelReason(cancelReasonId: string) {
    if (!this.customer$.value || !cancelReasonId) return;
    const currentReasons = [...this.cancelReasons$.value];
    let index = currentReasons.findIndex((cancelReason) => {
      return cancelReason.id === cancelReasonId;
    });
    currentReasons.splice(index, 1);
    this.cancelReasons$.next(currentReasons);

    Meteor.call(
      'removeCancelReason',
      this.customer$.value._id,
      cancelReasonId,
      (error: Meteor.Error) => {
        if (error) {
          this.toastService.add({
            type: 'ERROR',
            message: error.error?.toString(),
          });
        } else {
          this.toastService.add({ type: 'SUCCESS', message: 'changesSaved' });
        }
      },
    );
  }

  cancelChanges(cancelReasonId: string) {
    let index = this.initialCancelReasons.findIndex((cancelReason) => {
      return cancelReason.id === cancelReasonId;
    });
    const currentReasons = [...this.cancelReasons$.value];
    currentReasons[index].displayName =
      this.initialCancelReasons[index].displayName;
    currentReasons[index].enableDescription =
      this.initialCancelReasons[index].enableDescription;
    currentReasons[index].statusAfterCancel =
      this.initialCancelReasons[index].statusAfterCancel;
    this.cancelReasons$.next(currentReasons);
  }

  saveCancelReason(cancelReasonId: string): void {
    let cancelReason = this.cancelReasons$.value.find((cancelReason) => {
      return cancelReason.id === cancelReasonId;
    });
    if (!this.customer$.value || !cancelReason) return;
    Meteor.call(
      'updateCancelReason',
      this.customer$.value._id,
      cancelReason,
      (error: Meteor.Error) => {
        if (error) {
          this.toastService.add({
            type: 'ERROR',
            message: error.error?.toString(),
          });
        } else {
          this.toastService.add({ type: 'SUCCESS', message: 'changesSaved' });
        }
      },
    );
  }

  trackById(index: number, object: CancelReason): string {
    return object.id;
  }
}
